import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { UrlFlagsService } from '../../../feature-seatmap';

/**
 * Preserves URL context (query parameters and flags) across internal navigation.
 *
 * This guard preserves all query parameters from the current URL when navigating to a new route,
 * ensuring that context is maintained during navigation (e.g., via breadcrumb dropdowns).
 *
 * Specific handling for `embedded`/`host`:
 * - `embedded` and `host` are derived exclusively from the URL (see `UrlFlagsService`),
 *   so they no longer survive automatically via storage. This guard re-appends them to
 *   the target URL (via a redirect) whenever the previous navigation had them active but
 *   the new URL doesn't already carry them explicitly.
 *
 * General handling for all query parameters:
 * - All query params from the current URL are preserved on navigation to the target URL,
 *   unless explicitly already set in the target (including opt-outs like `param=` or `param=0`).
 *
 * Embedded state signalled by a `/embedded/` path segment doesn't need the query param
 * re-added, since the path itself keeps signalling embedded mode.
 *
 * A param that's already present in the target URL — even an explicit opt-out like
 * `embedded=0` or `host=` — is left untouched, so callers can still deliberately leave
 * embedded mode or clear the host (e.g. the "Open in Browser" workflow).
 */
function preserveContext(stateUrl: string): true | UrlTree {
  const urlFlags = inject(UrlFlagsService);
  const router = inject(Router);

  const urlTree = router.parseUrl(stateUrl);
  const targetQueryParams = { ...urlTree.queryParams };
  let changed = false;

  // Preserve all query params from the current URL that aren't explicitly set in target
  // (only if router.url is available - it may be undefined in test environments)
  if (router.url) {
    const currentUrlTree = router.parseUrl(router.url);
    const currentQueryParams = currentUrlTree.queryParams;

    for (const [key, value] of Object.entries(currentQueryParams)) {
      // `token` is consumed by authGuard and must not be re-appended (would cause redirect loops).
      // `returnUrl` belongs to the login page and may itself carry the token.
      if (key === 'token' || key === 'returnUrl') {
        continue;
      }
      if (!Object.hasOwn(targetQueryParams, key)) {
        targetQueryParams[key] = value;
        changed = true;
      }
    }
  }

  // Special handling for embedded and host (preserve from URL flags)
  const isEmbeddedPath = stateUrl.includes('/embedded/');
  const hasEmbeddedParam = Object.hasOwn(targetQueryParams, 'embedded');
  if (urlFlags.embedded() && !isEmbeddedPath && !hasEmbeddedParam) {
    targetQueryParams['embedded'] = '1';
    changed = true;
  }

  const host = urlFlags.host();
  const hasHostParam = Object.hasOwn(targetQueryParams, 'host');
  if (host && !hasHostParam) {
    targetQueryParams['host'] = host;
    changed = true;
  }

  if (!changed) {
    return true;
  }

  urlTree.queryParams = targetQueryParams;
  return urlTree;
}

export const preserveContextGuard: CanActivateFn = (_route, state) => preserveContext(state.url);

export const preserveContextChildGuard: CanActivateChildFn = (_route, state) =>
  preserveContext(state.url);
